import React from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useTranslation } from "@emisgroup/acp-application-intl";
import {
  Environments,
  RecurrenceType,
  SaveSchedule,
  SchedulePayload
} from "@emisgroup/cohort-builder-kit";
import { setNotification } from "../../../../utilities/HttpRequest/Script";
import { FEASIBILITY_APP_ID } from "../../../LeftPanel/Constant";
import { NotificationType } from "../../../Notification/redux/Types";
import { ProgressSpinnerSlice } from "../../../ProgressSpinner/Index";
import { IRootState } from "../../../../reducer/CombineReducer";
import { useUser } from "../../../../AuthContext";
import { handleException } from "../../../../ErrorMessages/helper";
import { CreateScheduleView } from "./CreateSchedule.view";
import { CreateScheduleSlice } from "./redux";
import { ScheduleDetails, SchedulingModes } from "./redux/Types";

// Responsible for rendering create schedule form
export const CreateSchedule: React.FC = () => {
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  const [
    extractState,
    scheduling
  ] = useSelector((state: IRootState) => [
    state.extractState,
    state.scheduling
  ], shallowEqual);
  const scheduleFormData = scheduling.schedulingDetails;
  const extractId = extractState.selectedExtract?.extractGuid as string;
  const { user } = useUser();
  const env = process.env.APP_TARGET_ENV as Environments;

  // Called when clicking of cancel and back in create schedule screen
  const goBack = (): void => {
    dispatch(CreateScheduleSlice.actions.setScheduleListPage(false));
    dispatch(CreateScheduleSlice.actions.seSchedulingMode(SchedulingModes.VIEW));
    dispatch(CreateScheduleSlice.actions.setIsValidScheduleName(true));
  };

  // Called while clicking save button
  const handleOnSave = async (): Promise<void> => {
    try {
      dispatch(ProgressSpinnerSlice.actions.showProgressBar(translate("Notification.SavingScheduleConfig")));

      // Remove a single key field
      const payload: SchedulePayload = {
        applicationGuid: FEASIBILITY_APP_ID,
        name: scheduleFormData?.scheduleName,
        recurrenceType: scheduleFormData?.recurrenceType as RecurrenceType,
        recurrenceData: {
          recurrenceRangeStart: scheduleFormData?.recurrenceRangeStart || "",
          recurrenceRunTime: scheduleFormData?.recurrenceRunTime,
        },
        isActive: scheduleFormData?.activeInLive || false,
        executionTargetParameters: {
          targetType: "CDB",
          targetValue: ["181716", "181617"],
        },
      };

      if (scheduleFormData?.recurrenceRangeEnd) {
        payload.recurrenceData.recurrenceRangeEnd = scheduleFormData.recurrenceRangeEnd;
      } else {
        payload.recurrenceData.recurrenceNoOfOccurrences = Math.max(
          Number(scheduleFormData?.recurrenceNoOfOccurrences) || 1, 1
        );
      }

      if (scheduleFormData?.recurrenceType !== RecurrenceType.DAILY && scheduleFormData?.recurrenceData) {
        const { day, month, calculation, selectedWeekDays } = scheduleFormData.recurrenceData;
        payload.recurrenceData.recurrenceCalculationMeta = {};
        const { recurrenceCalculationMeta } = payload.recurrenceData;

        if (day) recurrenceCalculationMeta.day = day;
        if (month) recurrenceCalculationMeta.month = month;
        if (calculation)
          recurrenceCalculationMeta.calculation = calculation.map((item: string | number) => Number(item));
        if (selectedWeekDays) recurrenceCalculationMeta.selectedWeekDays = selectedWeekDays;
      }

      const response = await SaveSchedule(
        extractId,
        payload,
        env,
        user?.token
      );

      if (response && response["data"] && response["data"]["payload"]) {
        const { scheduleInstances, ...rest } = response["data"] && response["data"]["payload"];

        const scheduleDetails: ScheduleDetails = {
          extractGuid: rest?.artefactGuid,
          scheduleGuid: rest?.id,
          scheduleName: rest?.scheduleName,
          activeInLive: rest?.isActive,
          recurrenceType: rest?.recurrenceType,
          recurrenceRunTime: rest?.recurrenceData?.recurrenceRunTime,
          recurrenceData: {
            ...(rest?.recurrenceData?.recurrenceCalculationMeta?.month && {
              month: rest?.recurrenceData?.recurrenceCalculationMeta?.month,
            }),
            ...(rest?.recurrenceData?.recurrenceCalculationMeta?.day && {
              day: rest?.recurrenceData?.recurrenceCalculationMeta?.day,
            }),
            ...(rest?.recurrenceData?.recurrenceCalculationMeta?.selectedWeekDays && {
              selectedWeekDays: rest?.recurrenceData?.recurrenceCalculationMeta?.selectedWeekDays,
            }),
            ...(rest?.recurrenceData?.recurrenceCalculationMeta?.calculation && {
              calculation: rest?.recurrenceData?.recurrenceCalculationMeta?.calculation,
            }),
          },
          recurrenceRangeStart: rest?.recurrenceData?.recurrenceRangeStart,
          ...(rest?.recurrenceData?.recurrenceRangeEnd && {
            recurrenceRangeEnd: rest?.recurrenceData?.recurrenceRangeEnd,
          }),
          ...(rest?.recurrenceData?.recurrenceNoOfOccurrences && {
            recurrenceNoOfOccurrences: rest?.recurrenceData?.recurrenceNoOfOccurrences
          }),
        };

        dispatch(CreateScheduleSlice.actions.setNewScheduleDetails(scheduleDetails));
        dispatch(CreateScheduleSlice.actions.setscheduleInstances(scheduleInstances));
        dispatch(CreateScheduleSlice.actions.seSchedulingMode(SchedulingModes.VIEW));
      }
      dispatch(ProgressSpinnerSlice.actions.hideProgressBar());
      setNotification(
        dispatch,
        "create-schedule",
        translate("Notification.ScheduleSuccess"),
        NotificationType.Confirmation
      );
    } catch (ex) {
      if (typeof (ex) === "object" && ex.data.errors &&
        ex.data.errors[0].detail[0].msg === "Schedule name already exists") {
        dispatch(CreateScheduleSlice.actions.setIsValidScheduleName(false));
        dispatch(ProgressSpinnerSlice.actions.hideProgressBar());
      } else {
        handleException(ex, dispatch, "SaveScheduleConfigErrMsg");
      }
    }
  };

  // Invoked when clicking the edit button and will enable
  const onEditClick = (): void => {
    dispatch(CreateScheduleSlice.actions.seSchedulingMode(SchedulingModes.EDIT));
  };

  return (
    <>
      <CreateScheduleView
        onClick={goBack}
        onSave={handleOnSave}
        onEditClick={onEditClick}
      />
    </>
  );
};
